import React from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { SectionHeading } from "components/misc/Headings.js";
import { SectionDescription } from "components/misc/Typography.js";
import { PrimaryButton as PrimaryButtonBase } from "components/misc/Buttons.js";
import { Container as ContainerBase, ContentWithPaddingXl as ContentBase } from "components/misc/Layouts.js";
import { ReactComponent as CheckboxIcon } from "images/checkbox-circle.svg";

const Container = tw(ContainerBase)`bg-[#1B263B] text-gray-100 -mx-8 px-8`;
const ContentWithPaddingXl = tw(
  ContentBase
)`relative z-10 mx-auto px-0 py-10 sm:px-6 md:px-8 lg:px-12 xl:px-24 sm:py-20 flex flex-col max-w-screen-xl`;
const HeaderContainer = tw.div`mt-10 w-full flex flex-col items-center`;
const Heading = tw(SectionHeading)`w-full text-white`;
const Description = tw(SectionDescription)`w-full text-gray-300 text-center`;

const PlansContainer = tw.div`mt-16 flex flex-col items-center xl:flex-row xl:items-stretch mx-3 sm:-mx-72 xl:justify-between text-gray-900 font-medium`;

const Plan = styled.div`
  ${tw`w-full sm:w-[55%] bg-white rounded-6xl shadow-sm py-10 px-6 sm:px-10 lg:px-6 xl:py-10 xl:p-10 mx-3 flex flex-col justify-between mt-16 first:mt-0 xl:mt-0`}
`;

const PlanHeader = styled.div`
  .nameAndFeaturedContainer {
    ${tw`flex flex-wrap flex-col sm:flex-row justify-between items-center`}
  }
  .name {
    ${tw`lg:text-lg xl:text-xl font-bold uppercase tracking-wider mr-3 text-[#3F62A3]`}
  }
  .featuredText {
    ${tw`text-xs font-bold px-3 rounded py-2 uppercase bg-blue-200 text-blue-900 leading-none mt-4 sm:mt-0 w-full sm:w-auto text-center`}
  }
  .pricingContainer {
    ${tw`mt-6 flex items-end justify-between`}
    .currentPrice {
      ${tw`text-lg font-bold leading-none text-[#1B263B]`}
      .bigText {
        ${tw`text-3xl font-bold`}
      }
    }
    .oldPrice {
      ${tw`text-gray-400 text-lg line-through hidden sm:block`}
    }
  }
  .description {
    ${tw`mt-8 font-medium text-gray-700 lg:text-sm xl:text-base`}
  }
`;

const PlanFeatures = styled.ul`
  ${tw`mt-10 flex-1 border-t lg:-mx-6 -mx-6 sm:-mx-10 py-10 px-6 sm:px-10 lg:p-6 xl:-mx-10 xl:p-10`}
  .feature {
    ${tw`flex items-start mt-6 first:mt-0`}
    .icon {
      ${tw`w-6 h-6 text-[#3F62A3] flex-shrink-0`}
    }
    .text {
      ${tw`font-semibold text-gray-700 tracking-wide ml-3`}
    }
  }
`;

const PlanAction = tw.div`mt-4`;
const ActionButton = styled(PrimaryButtonBase)`
  ${tw`block text-center text-sm font-semibold tracking-wider w-full text-gray-100 bg-[#3F62A3] px-6 py-4 rounded-full hover:bg-[#2d4b7a] focus:shadow-outline focus:outline-none transition-colors duration-300`}
`;

const WhiteBackgroundOverlay = tw.div`absolute inset-x-0 bottom-0 h-1/6 lg:h-1/3 bg-white z-0`;

export default ({
  description = "Bekijk onze populaire slotenmakersdiensten en beveiligingsoplossingen. Bij Safety Locks bieden we topkwaliteit montage, SKG-gecertificeerde producten en eerlijke prijzen.",
  plans = null,
  primaryButtonText = "Vraag offerte aan",
}) => {
  const defaultPlans = [
    {
      name: "Kerntrekbeslag",
      price: ["€139", ",50"],
      oldPrice: "€170,50",
      description: "Kerntrekbeslag F1 geborsteld staal, inclusief montage en SKG***® keurmerk voor maximale beveiliging.",
      features: [
        "Merken: Lips / Hoppe / Dieckmann / VOS",
        "Kerntrekbeslag F1 geborsteld staal",
        "Inclusief montage",
        "SKG***® keurmerk",
      ],
      url: "/offerte-aanvragen",
    },
    {
      name: "Spoed Deuropening",
      price: ["€90", ",00"],
      oldPrice: "€100,00",
      description: "Snelle en schadevrije deuropening bij buitensluiting. 24/7 beschikbaar.",
      features: [
        "Direct ter plaatse bij noodgevallen",
        "Geen schade aan deuren of sloten",
        "Altijd 24/7 bereikbaar",
        "Gratis advies over verbeterde beveiliging",
      ],
      url: "/offerte-aanvragen",
    },
    {
      name: "Meerpuntssluiting",
      price: ["€290", ",00"],
      oldPrice: "€350,50",
      description: "Professionele meerpuntssluiting inclusief montage, sluitplaten en cilinder met sleutels.",
      features: [
        "Merken: KFV / LIPS / HMB / GU",
        "Incl. sluitkommen en platen",
        "Cilinder 30/30 SKG**® met 3 sleutels",
        "Volledige montage inbegrepen",
      ],
      url: "/offerte-aanvragen",
      featured: "Populairste Keuze",
    },
    {
      name: "Gecertificeerde Cilinder",
      price: ["€100", ",00"],
      oldPrice: "€150,50",
      description: "Cilinder met veiligheidscertificaat en anti-breek brug, professioneel geplaatst.",
      features: [
        "Merken: Dom / Lips / Nemef / Winkhaus",
        "Incl. veiligheidscertificaat",
        "Incl. anti-breek brug",
        "Incl. montage",
        "Incl. 3 sleutels (30/30 cilinder)",
      ],
      url: "/offerte-aanvragen",
    },
  ];

  if (!plans) plans = defaultPlans;

  return (
    <Container id="aanbiedingen">
      <ContentWithPaddingXl>
        <HeaderContainer>
          <Heading>Onze slotenmakersdiensten en aanbiedingen</Heading>
          {description && <Description>{description}</Description>}
        </HeaderContainer>
        <PlansContainer>
          {plans.map((plan, index) => (
            <Plan key={index} featured={plan.featured}>
              <PlanHeader>
                <span className="nameAndFeaturedContainer">
                  <span className="name">{plan.name}</span>
                  {plan.featured && <span className="featuredText">{plan.featured}</span>}
                </span>
                <div className="pricingContainer">
                  <span className="currentPrice">
                    <span className="text-md">vanaf </span>
                    <span className="bigText">{plan.price[0]}</span>
                    {plan.price[1]}{" "}
                  </span>
                  {plan.oldPrice && <span className="oldPrice">{plan.oldPrice}</span>}
                </div>
                <p className="description">{plan.description}</p>
              </PlanHeader>
              <PlanFeatures>
                {plan.features.map((feature, index) => (
                  <li className="feature" key={index}>
                    <CheckboxIcon className="icon" />
                    <span className="text">{feature}</span>
                  </li>
                ))}
              </PlanFeatures>
              <PlanAction>
                <ActionButton as="a" href={plan.url}>
                  {primaryButtonText}
                </ActionButton>
              </PlanAction>
            </Plan>
          ))}
        </PlansContainer>
      </ContentWithPaddingXl>
      <WhiteBackgroundOverlay />
    </Container>
  );
};
